/**
 * Upload Notification Component
 * User notifications for upload failures and retry options
 * Sprint 6 - Phase 1 Implementation
 */

import React from 'react';
import { notification, Button, Space, Typography, Progress } from 'antd';
import {
  ExclamationCircleOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { getRetryManager } from '../services/retryManager';
import logger from '../utils/logger';

const { Text } = Typography;

export interface UploadNotificationProps {
  videoGuid: string;
  chunkNo?: number;
  error?: string;
  onRetry: (videoGuid: string, chunkNo?: number) => void;
  onDiscard: (videoGuid: string) => void;
}

export const showUploadFailureNotification = ({
  videoGuid,
  chunkNo,
  error,
  onRetry,
  onDiscard,
}: UploadNotificationProps) => {
  const retryManager = getRetryManager();
  const retryState = retryManager.getRetryState(videoGuid, chunkNo);
  const isMaxRetries = retryManager.isMaxRetriesExceeded(videoGuid, chunkNo);

  const chunkLabel = chunkNo !== undefined ? `chunk ${chunkNo}` : 'upload';
  const attempts = retryState?.attempts || 0;
  const maxAttempts = 10;

  if (isMaxRetries) {
    // Max retries exceeded - show critical notification
    notification.error({
      message: 'Upload Failed',
      description: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {chunkLabel} failed after {maxAttempts} attempts.
          </Text>
          {error && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Error: {error}
            </Text>
          )}
          <Text type="warning">
            You can retry manually or discard this recording.
          </Text>
        </Space>
      ),
      duration: 0, // Don't auto-close
      placement: 'topRight',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      btn: (
        <Space>
          <Button
            size="small"
            onClick={() => {
              notification.destroy();
              onDiscard(videoGuid);
            }}
            danger
          >
            <CloseCircleOutlined /> Discard
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy();
              retryManager.clearRetryState(videoGuid, chunkNo);
              onRetry(videoGuid, chunkNo);
            }}
          >
            <ReloadOutlined /> Retry Now
          </Button>
        </Space>
      ),
    });
  } else {
    // Regular retry notification
    const nextRetryDelay = retryManager.getTimeUntilNextRetry(videoGuid, chunkNo);
    const nextRetrySeconds = Math.ceil(nextRetryDelay / 1000);

    notification.warning({
      message: 'Upload Issue Detected',
      description: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {chunkLabel} failed (attempt {attempts}/{maxAttempts})
          </Text>
          {error && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {error}
            </Text>
          )}
          <Space align="center" style={{ width: '100%' }}>
            <Progress
              percent={Math.round((attempts / maxAttempts) * 100)}
              size="small"
              status="exception"
              style={{ flex: 1 }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {nextRetrySeconds > 0
                ? `Retrying in ${nextRetrySeconds}s`
                : 'Retrying now...'}
            </Text>
          </Space>
        </Space>
      ),
      duration: nextRetrySeconds > 0 ? Math.min(nextRetrySeconds, 10) : 5,
      placement: 'topRight',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      btn: nextRetryDelay > 5000 ? (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            notification.destroy();
            retryManager.clearRetryState(videoGuid, chunkNo);
            onRetry(videoGuid, chunkNo);
          }}
        >
          <ReloadOutlined /> Retry Now
        </Button>
      ) : undefined,
    });
  }

  logger.info(`Showed upload failure notification for ${chunkLabel}`, {
    attempts,
    maxRetries: isMaxRetries,
    error,
  });
};

export const showUploadSuccessNotification = (videoGuid: string, isBlankMode: boolean = false) => {
  notification.success({
    message: isBlankMode ? 'Recording Uploaded' : 'Upload Complete',
    description: isBlankMode
      ? 'Your video will be processed and available in your personal library soon.'
      : 'Your recording has been uploaded successfully.',
    duration: isBlankMode ? 5 : 3,
    placement: 'topRight',
  });

  logger.info(`Showed upload success notification for ${videoGuid} (blankMode=${isBlankMode})`);
};

export const showRetryInProgressNotification = (
  videoGuid: string,
  totalChunks: number,
  retryingChunks: number
) => {
  notification.info({
    message: 'Retry in Progress',
    description: (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text>
          Retrying {retryingChunks} of {totalChunks} chunks...
        </Text>
        <Progress
          percent={Math.round(((totalChunks - retryingChunks) / totalChunks) * 100)}
          size="small"
        />
      </Space>
    ),
    duration: 3,
    placement: 'topRight',
  });

  logger.info(`Showed retry in progress notification for ${videoGuid}`, {
    totalChunks,
    retryingChunks,
  });
};

export const showNetworkOfflineNotification = () => {
  notification.warning({
    message: 'Network Offline',
    description: 'Your connection is offline. Uploads will resume when connection is restored.',
    duration: 0, // Don't auto-close
    placement: 'topRight',
    key: 'network-offline', // Use key to prevent duplicates
  });
};

export const showNetworkOnlineNotification = () => {
  notification.destroy('network-offline');
  notification.success({
    message: 'Network Restored',
    description: 'Connection restored. Resuming uploads...',
    duration: 3,
    placement: 'topRight',
  });
};
